import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { db } from '../../config/firebase';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import {
  formatFeatureNotifyTime,
  groupFeatureNotifySignups,
  subscribeFeatureNotifySignups,
} from '../../utils/featureNotifyService';
import { AdminSectionHeader } from './AdminSectionHeader';

function uniqueEmails(rows) {
  return [...new Set(rows.map((row) => String(row.email || '').trim()).filter(Boolean))];
}

export function AdminFeatureNotify() {
  const { styles: adminStyles, colors } = useAdminThemeContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [featureFilter, setFeatureFilter] = useState('all');
  const [copyNote, setCopyNote] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const unsub = subscribeFeatureNotifySignups(
      db,
      (next) => {
        setRows(next);
        setLoadError('');
        setLoading(false);
      },
      (err) => {
        setLoadError(err?.message || 'Could not load notify-me emails.');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const groups = useMemo(() => groupFeatureNotifySignups(rows), [rows]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (featureFilter !== 'all' && String(row.feature || '') !== featureFilter) return false;
      if (!needle) return true;
      const hay = `${row.email || ''} ${row.feature || ''} ${row.uid || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [featureFilter, query, rows]);

  const visibleGroups = useMemo(() => groupFeatureNotifySignups(filtered), [filtered]);
  const emails = useMemo(() => uniqueEmails(filtered), [filtered]);

  const copyEmails = async () => {
    const text = emails.join(', ');
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setCopyNote(emails.length ? `Copied ${emails.length} email${emails.length === 1 ? '' : 's'}.` : 'No emails to copy.');
    } catch (err) {
      setCopyNote(err?.message || 'Could not copy emails.');
    }
  };

  const chipStyle = (active) => ({
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: active ? colors.navActive : colors.cardBorder,
    backgroundColor: active ? colors.navActive : colors.cardBgSubtle,
  });

  return (
    <View style={{ width: '100%', maxWidth: 1100, paddingBottom: 40 }}>
      <AdminSectionHeader
        title="Notify me signups"
        subtitle="Emails people left on hamburger or coming-soon pages so they can be told when that page goes live."
        isFirst
      />

      <Text style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 14, lineHeight: 20 }}>
        {loading ? 'Loading…' : `${rows.length} signup${rows.length === 1 ? '' : 's'} across ${groups.length} page${groups.length === 1 ? '' : 's'}.`}
      </Text>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <TouchableOpacity onPress={() => setFeatureFilter('all')} style={chipStyle(featureFilter === 'all')}>
          <Text style={{ color: featureFilter === 'all' ? colors.textOnAccent : colors.textPrimary, fontWeight: '700', fontSize: 12 }}>
            All ({rows.length})
          </Text>
        </TouchableOpacity>
        {groups.map((group) => (
          <TouchableOpacity
            key={group.feature}
            onPress={() => setFeatureFilter(group.feature)}
            style={chipStyle(featureFilter === group.feature)}
          >
            <Text style={{ color: featureFilter === group.feature ? colors.textOnAccent : colors.textPrimary, fontWeight: '700', fontSize: 12 }}>
              {group.feature} ({group.count})
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 18 }}>
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search email, page, or user id"
          placeholderTextColor={colors.inputPlaceholder}
          style={[adminStyles.formInput, { marginBottom: 0, minWidth: 240, flex: 1 }]}
        />
        <TouchableOpacity
          onPress={copyEmails}
          style={{
            paddingVertical: 12,
            paddingHorizontal: 16,
            backgroundColor: colors.navActive,
            borderRadius: 8,
          }}
        >
          <Text style={{ color: colors.textOnAccent, fontWeight: '700' }}>Copy emails</Text>
        </TouchableOpacity>
      </View>
      {copyNote ? (
        <Text style={{ color: colors.textSecondary, fontSize: 12, marginBottom: 12 }}>{copyNote}</Text>
      ) : null}

      {loading ? (
        <ActivityIndicator size="large" color={colors.accentGreen} />
      ) : loadError ? (
        <Text style={{ color: colors.textSecondary, fontSize: 14 }}>{loadError}</Text>
      ) : filtered.length === 0 ? (
        <Text style={{ color: colors.textSecondary, fontSize: 14 }}>
          No notify-me emails yet. They appear here after someone submits “Notify me when this is live.”
        </Text>
      ) : (
        visibleGroups.map((group) => (
          <View
            key={group.feature}
            style={{
              backgroundColor: colors.cardBg,
              borderRadius: 10,
              padding: 14,
              marginBottom: 14,
              borderWidth: 1,
              borderColor: colors.cardBorder,
            }}
          >
            <Text style={[adminStyles.cardTitle, { marginBottom: 10 }]}>
              {group.feature}
              <Text style={{ color: colors.textMuted, fontWeight: '600' }}> · {group.count}</Text>
            </Text>
            {group.signups.map((row) => (
              <View
                key={row.id || `${row.email}-${row.createdAt}`}
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 8,
                  paddingVertical: 8,
                  borderTopWidth: 1,
                  borderTopColor: colors.divider,
                }}
              >
                <Text style={{ color: colors.textPrimary, fontWeight: '700', minWidth: 200, flex: 1 }}>
                  {row.email || '—'}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13, minWidth: 160 }}>
                  {formatFeatureNotifyTime(row.createdAt)}
                </Text>
                {row.uid ? (
                  <Text style={{ color: colors.textMuted, fontSize: 11 }}>{row.uid}</Text>
                ) : (
                  <Text style={{ color: colors.textMuted, fontSize: 11 }}>signed out</Text>
                )}
              </View>
            ))}
          </View>
        ))
      )}
    </View>
  );
}
